import React from "react";
import {database, globalSettings} from "./config";
import {GlobalInfo} from "./context";
import {numberWithCommas, renderShortDate, toTitleCase} from "./util";
import {calculatePricingInfo, calculateRefine, newTransaction} from "./model/transaction";
import {toast} from "react-toastify";
import * as firebase from "firebase/app";
import {Card} from "react-bootstrap";
import {FinanceOnly, If} from "./Common";
import {withTranslation} from "react-i18next";


const MODE_VIEW = 0;
const MODE_LOG = 1;
const MODE_SET_PRICE = 2;
const MODE_SET_TARGET = 3;
const MODE_SET_INVENTORY = 4;
const MODE_SET_MULTIPLIER = 5;
const MODE_SET_ORDER = 6;
const MODE_SET_REFINE_MULTIPLIER = 7;
const MODE_SET_BUFFER = 8;

// German game client → English DB name mapping for paste support
const GERMAN_MATERIAL_NAMES = {
  'Wertmetall': 'Precious Metals',
  'Nicht-kubischer Kristall': 'Non-CS Crystals',
};

// German prefix mappings for ore variants (e.g., "Reichhaltiges Kernite" → "Rich Kernite")
const GERMAN_PREFIXES = {
  'Reichhaltiges ': 'Rich ',
  'Reichhaltiger ': 'Rich ',
  'Reichhaltige ': 'Rich ',
};

function translateMaterialName(name) {
  if (GERMAN_MATERIAL_NAMES[name]) {
    return GERMAN_MATERIAL_NAMES[name];
  }
  for (const [prefix, replacement] of Object.entries(GERMAN_PREFIXES)) {
    if (name.startsWith(prefix)) {
      return replacement + name.substring(prefix.length);
    }
  }
  return name;
}

class MaterialsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: MODE_VIEW,
      noPayment: false,
      paymentType: "isk",
      newData: {},
      tab: "Minerals",
      location: localStorage.getItem("lastLocation") || globalSettings.defaultLocation,
      pasteArea: ""
    }
  }

  // componentDidMount() {
  //   database.collection("materials").onSnapshot((querySnapshot) => {
  //     let newMaterials = [];
  //     querySnapshot.forEach(function (doc) {
  //       newMaterials.push(doc.data());
  //     });
  //     newMaterials.sort((a, b) => (a.order || 99) - (b.order || 99));
  //     this.setState({
  //       materials: newMaterials
  //     });
  //   });
  // }

  changeData(name, e) {
    if (e.target.value == "") {
      let newData = this.state.newData;
      delete newData[name];
      this.setState({
        newData: newData
      })
    } else {
      let parsedValue = parseInt(e.target.value);
      if (isNaN(parsedValue)) {
        return;
      }
      if (parsedValue < 0) {
        parsedValue = 0;
      }
      let newData = this.state.newData;
      newData[name] = parsedValue;
      this.setState({
        newData: newData
      })
    }
  }

  setLocation(e) {
    this.setState({
      location: e.target.value
    });
    localStorage.setItem("lastLocation", e.target.value)
  }

  getPrice(material) {
    return calculatePricingInfo(material, this.context.materialsMap, this.state.location).adjustedPrice;
  }

  getPerDensityPrice(material) {
    return this.getPrice(material) / material.density
  }

  getPerHourPrice(material) {
    return this.getPrice(material) * material.perfect_output
  }

  save() {
    switch (this.state.mode) {
      case MODE_LOG:
        return this.logTransaction();
      case MODE_SET_PRICE:
        return this.setMaterialData("price");
      case MODE_SET_TARGET:
        return this.setMaterialData("totalTargetInventory");
      case MODE_SET_BUFFER:
        return this.setMaterialData("totalBuffer");
      case MODE_SET_INVENTORY:
        return this.setMaterialData(`inventory.${this.state.location}`);
      case MODE_SET_ORDER:
        return this.setMaterialData("order");
      case MODE_SET_MULTIPLIER:
        return this.setMaterialData("multiplier");
      case MODE_SET_REFINE_MULTIPLIER:
        return this.setMaterialData("refineMultiplier");
      default:
        return
    }
  }

  setMaterialData(keyToUpdate) {
    let newData = this.state.newData;
    if (keyToUpdate.indexOf("inventory.") !== -1) {
      let [refined] = calculateRefine(newData, this.context.materialsMap);
      for (let [name, value] of Object.entries(refined)) {
        newData[name] = value;
      }
    }

    let batch = database.batch();
    for (let [name, value] of Object.entries(newData)) {
      let doc = database.collection("materials").doc(name);
      let updates = {
        [keyToUpdate]: value,
      };
      // if one location inventory updated, update sum location as well
      if (keyToUpdate.indexOf("inventory.") !== -1) {
        updates["totalInventory"] = firebase.firestore.FieldValue.increment(value - (this.context.materialsMap[name].inventory[this.state.location] || 0))
      }
      if(keyToUpdate === "price") {
        updates["priceUpdated"] = +(Date.now());
        updates["priceUpdatedBy"] = this.context.userInfo;
      }
      batch.update(doc, updates);
      // if(keyToUpdate === "price") {
      //   let newPriceRef = doc.collection("prices").doc();
      //   batch.set(newPriceRef, {
      //     "timestamp": Date.now(),
      //     "userName": this.context.userData.name,
      //     "userId": this.context.userData.id,
      //     "price": value,
      //   })
      // }
    }
    return batch.commit().then(() => {
      this.setMode(MODE_VIEW)
    }).catch((error) => toast.error(error));
  }

  logTransaction() {
    const { t } = this.props;
    if (this.state.location === "") {
      toast.error(t('common:errors.selectLocation'));
      return
    }
    newTransaction(this.context.userData, this.state.newData, this.state.location, this.context.materialsMap).then((result) => {
      this.setMode(MODE_VIEW);
    })
  }

  setMode(newMode) {
    this.setState({
      mode: newMode,
      newData: {}
    })
  }

  handlePaste(e) {
    const { t } = this.props;
    const pastedText = e.target.value;
    this.setState({ pasteArea: pastedText });

    // Parse the pasted inventory data
    const lines = pastedText.split('\n');
    let newData = {};
    let unmatchedNames = [];

    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const [, rawName, quantity] = line.split('\t');
      if (!rawName || !quantity) continue;

      const name = rawName.trim();
      const parsedQuantity = parseInt(quantity);
      if (isNaN(parsedQuantity)) continue;

      // Translate German game client names to English DB names
      const translatedName = translateMaterialName(name);

      // Skip materials not found in the database
      if (this.context.materialsMap && !this.context.materialsMap[translatedName]) {
        unmatchedNames.push(name);
        continue;
      }

      newData[translatedName] = parsedQuantity;
    }

    // Update the form with parsed quantities
    this.setState({ newData });

    // Warn about unrecognized material names
    if (unmatchedNames.length > 0) {
      toast.warn(
        <div>
          <strong>{t('common:errors.unmatchedMaterials')}</strong>
          <ul>
            {unmatchedNames.map(n => <li key={n}>{n}</li>)}
          </ul>
          <p>{t('common:errors.contactForHelp')}</p>
        </div>,
        { autoClose: false }
      );
    }
  }

  renderMaterials(materials) {
    if (materials && this.context.materialsMap) {
      if (this.state.tab == "Minerals") {
        return (<div className="row">
          <div className="col-lg">
            {this.renderMaterialsTable("Minerals", materials.filter((m) => m.type === 'mineral'))}
          </div>
          <div className="col-lg col-lg-8">
            {this.renderMaterialsTable("ore", materials.filter((m) => m.type === 'ore'))}
          </div>
        </div>)
      } else if (this.state.tab === "Planetary") {
        return (<div className="row">
          <div className="col-lg">
            {this.renderMaterialsTable("Planetary", materials.filter((m) => m.type === 'planetary'))}
          </div>
        </div>)
      } else if (this.state.tab === "Datacores") {
        return (<div className="row">
          <div className="col-lg">
            {this.renderMaterialsTable("Faction", materials.filter((m) => m.type === 'datacore' && m.order < 205))}
          </div>
          <div className="col-lg">
            {this.renderMaterialsTable("Other", materials.filter((m) => m.type === 'datacore' && m.order >= 205))}
          </div>
          <div className="col-lg">
          </div>
        </div>)
      } else if (this.state.tab === "Debris") {
        return (<div className="row">
          <div className="col-lg">
            {this.renderMaterialsTable("Faction", materials.filter((m) => m.type === 'debris'))}
          </div>
        </div>)
      } else {
        return null
      }

    } else {
      return <p>Loading...</p>
    }
  }

  getImage(material) {
    if (material.type === "datacore") {
      return "/images/datacore.png"
    }
    if (material.type === "debris") {
      return "/images/debris.png"
    }
    return `/images/${material.name.toLowerCase().replace(/ /g, '-')}.png`
  }

  renderMaterialsTable(category, materials) {
    if (category.indexOf("ore") !== -1 && (this.state.mode === MODE_VIEW /*|| this.state.mode === MODE_LOG*/)) {
      materials.sort((a, b) =>
          this.getPerDensityPrice(b) - this.getPerDensityPrice(a)
      )
    }

    if (this.state.tab === "Planetary" && (this.state.mode === MODE_VIEW /*|| this.state.mode === MODE_LOG*/)) {
      materials.sort((a, b) =>
          this.getPerHourPrice(b) - this.getPerHourPrice(a)
          // (b.totalTargetInventory || 0) - (a.totalTargetInventory || 0) + (a.order || 0) - (b.order || 0)
      )
    }

    // return <div className="card">
    //   <h5 className="card-header">
    //     {category}
    //   </h5>
    return <div className={"material-group"}>
      {/*<table className="table">*/}
      {materials.map((material) => {
        let pricingInfo = calculatePricingInfo(material, this.context.materialsMap, this.state.location);
        return <div className={"material card"} key={material.name}>
          <div className={"material-name"}>{material.name}</div>
          <img src={this.getImage(material)} width={185} onError={(evt) => evt.target.style.display = 'none'}/>
          {this.renderMaterialsCell(material)}
          {((this.state.mode === MODE_VIEW || this.state.mode === MODE_SET_PRICE) && material.type !== "ore") ?
              <div className={"material-inventory"}>
                <div className={`progress ${pricingInfo.demandClass}`}>
                  <div className="progress-bar" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"
                       style={{"width": pricingInfo.percentToNextDemandClass + '%'}}>
                  </div>
                  <div className="progress-label">
                    {numberWithCommas(pricingInfo.inventory || 0)} / {numberWithCommas(pricingInfo.targetInventory || 0)}
                  </div>
                </div>
              </div> : null}
        </div>
      })}
    </div>;
    {/*</table>*/
    }
    // </div>
  }

  renderMaterialsCell(material) {
    let totalPrice = this.getPrice(material);
    let iskPrice = this.getPrice(material) * (1 - globalSettings.pandacoinRatio);
    let pandaPrice = this.getPrice(material) * globalSettings.pandacoinRatio;

    if (this.state.mode === MODE_VIEW) {
      // if(material.type === "ore") {
      //   return <td>
      //     <div className={"price"}>
      //       <div className="main-price">{Math.round(price)} ISK</div>
      //       <div className="sub-price">
      //         {Math.floor(price / material.density)} ISK / m³
      //       </div>
      //     </div>
      //   </td>
      // } else {
      return <div className={"price"}>
            <span className="main-price">
              {material.type === "ore" ? `${numberWithCommas(totalPrice / material.density)} / m³` : null}
              {material.type === "planetary" ? `${numberWithCommas(totalPrice * material.perfect_output)} / hr` : null}
              {(material.type !== "ore" && material.type !== "planetary") ? numberWithCommas(totalPrice) : null}
            </span>
        {(material.type === "ore" || material.type === "planetary") ? <span className="sub-price">({numberWithCommas(totalPrice)})</span> : null}
      </div>
      // }

    } else if (this.state.mode === MODE_LOG) {
      return <div className="form-group">
        <div className="input-group">
          <input type="number" className="form-control hide-arrow" id="exampleFormControlInput1"
                 placeholder={0} value={this.state.newData[material.name] || ""}
                 onChange={(e) => this.changeData(material.name, e)}/>
          <div className="input-group-append">
            <span className="input-group-text">units</span>
          </div>
        </div>
        <div className="sub-price">{numberWithCommas(iskPrice)} ISK + {numberWithCommas(pandaPrice)} 🐼</div>
        {/*<div><span className={"requested"}>{numberWithCommas(material.totalTargetInventory || 0)}</span> requested</div>*/}
      </div>
    } else if (this.state.mode === MODE_SET_PRICE) {
      return <div className="form-group">
        {material.type === "ore" ? null : <div className="input-group">
          <input type="number" className="form-control" id="exampleFormControlInput1"
                 placeholder={material.price} value={this.state.newData[material.name] || ""}
                 onChange={(e) => this.changeData(material.name, e)}
          />
          <div className="input-group-append">
            <span className="input-group-text">ISK</span>
          </div>
        </div>}
        <If cond={material.type !== "ore"}><div className={"sub-price"}>Updated: {renderShortDate(material.priceUpdated)}</div></If>
        {/* <div className={"sub-price"}>EEM: {numberWithCommas(material.eem_price)} ISK</div> */}
        <div className={"sub-price"}>EEM: {numberWithCommas(material.price)} ISK</div>
        {/*<div className="sub-price">*/}
        {/*  {material.type === "ore" ? `${numberWithCommas(totalPrice / material.density)} / m³` : null}*/}
        {/*  {material.type === "planetary" ? `${numberWithCommas(totalPrice * material.perfect_output)} / hr` : null}*/}
        {/*  {material.type === "mineral" ? numberWithCommas(totalPrice) : null}*/}
        {/*</div>*/}
      </div>
    } else if (this.state.mode === MODE_SET_TARGET) {
      return <div className="form-group">
        <input type="number" className="form-control" id="exampleFormControlInput1"
               placeholder={material.totalTargetInventory || 0} value={this.state.newData[material.name] === undefined ? "" : this.state.newData[material.name]}
               onChange={(e) => this.changeData(material.name, e)}
               disabled={material.type === "ore"}/>
      </div>
    } else if (this.state.mode === MODE_SET_BUFFER) {
      return <div>
        <div className="form-group">
          <input type="number" className="form-control" id="exampleFormControlInput1"
                 placeholder={material.totalBuffer || 0} value={this.state.newData[material.name] === undefined ? "" : this.state.newData[material.name]}
                 onChange={(e) => this.changeData(material.name, e)}
                 disabled={material.type === "ore"}/>
        </div>
        <div className={"sub-price"}>Current stock: {numberWithCommas(material.totalInventory)} / {numberWithCommas(material.totalTargetInventory)}</div>
      </div>
    } else if (this.state.mode === MODE_SET_INVENTORY) {
      return <div className="form-group">
        {this.state.location === "" ?
            numberWithCommas(material.totalInventory || 0)
            : <input type="number" className="form-control" id="exampleFormControlInput1"
                     placeholder={numberWithCommas(material.inventory[this.state.location] || 0)}
                     value={this.state.newData[material.name] === undefined ? "" : this.state.newData[material.name]}
                     onChange={(e) => this.changeData(material.name, e)}/>}

      </div>
    } else if (this.state.mode === MODE_SET_ORDER) {
      return <div className="form-group">
        <input type="number" className="form-control" id="exampleFormControlInput1"
               placeholder={material.order || 99} value={this.state.newData[material.name] === undefined ? "" : this.state.newData[material.name]}
               onChange={(e) => this.changeData(material.name, e)}/>
      </div>
    } else if (this.state.mode === MODE_SET_MULTIPLIER) {
      return <div className="form-group">
        <div className="input-group">
          <input type="number" className="form-control" id="exampleFormControlInput1"
                 placeholder={material.multiplier || 50} value={this.state.newData[material.name] === undefined ? "" : this.state.newData[material.name]}
                 onChange={(e) => this.changeData(material.name, e)}/>
          <div className="input-group-append">
            <span className="input-group-text">%</span>
          </div>
        </div>
        <div><span className={"requested"}>{numberWithCommas(material.totalTargetInventory || 0)}</span> requested</div>
      </div>
    } else if (this.state.mode === MODE_SET_REFINE_MULTIPLIER) {
      return <div className="form-group">
        <div className="input-group">
          <input type="number" className="form-control" id="exampleFormControlInput1"
                 placeholder={material.refineMultiplier || 50} value={this.state.newData[material.name] === undefined ? "" : this.state.newData[material.name]}
                 onChange={(e) => this.changeData(material.name, e)}
                 disabled={material.type !== "ore"}/>
          <div className="input-group-append">
            <span className="input-group-text">%</span>
          </div>
        </div>
      </div>
    }
  }

  renderToolbar() {
    const { t } = this.props;
    return <div>
      <div className="btn-toolbar mb-3" role="toolbar" aria-label="Toolbar with button groups">
        <div className={"input-group mr-2"}>
          <div className="input-group-prepend">
            <span className="input-group-text" id="">{t('common:labels.location')}</span>
          </div>
          <select className="form-control" value={this.state.location} onChange={e => this.setLocation(e)}>
            <option value="">{t('pages:materials.selectBase')}</option>
            {globalSettings.allowTransactionsAt.map((id) =>
                <option key={id} value={id}>{globalSettings.locations[id]}</option>
            )}
          </select>
        </div>
      </div>

      {this.state.mode === MODE_LOG && (
        <div className="form-group mb-3">
          <label>{t('pages:materials.pasteInventory')}:</label>
          <textarea
            className="form-control"
            rows="4"
            value={this.state.pasteArea}
            onChange={(e) => this.handlePaste(e)}
            placeholder={t('pages:materials.pasteInventoryPlaceholder')}
          />
        </div>
      )}

      <div className="btn-toolbar mb-3" role="toolbar" aria-label="Toolbar with button groups">
        <If cond={this.state.mode === MODE_VIEW}>
          <div className="btn-group mr-2" role="group" aria-label="First group">
            <button type="button" className="btn btn-primary" onClick={() => this.setMode(MODE_LOG)}>{t('pages:materials.buttons.beginTransaction')}</button>
          </div>
          <FinanceOnly>
            <div className="btn-group mr-2" role="group" aria-label="First group">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_PRICE)}>{t('pages:materials.buttons.setPrices')}</button>
            </div>
            <div className="btn-group mr-2" role="group" aria-label="First group">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_MULTIPLIER)}>{t('pages:materials.buttons.setMultipliers')}</button>
            </div>
            <div className="btn-group mr-2">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_TARGET)}>{t('pages:materials.buttons.setTargets')}</button>
            </div>
            <div className="btn-group mr-2">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_BUFFER)}>{t('pages:materials.buttons.setBuffer')}</button>
            </div>
            <div className="btn-group mr-2">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_INVENTORY)}>{t('pages:materials.buttons.setInventory')}</button>
            </div>
            <div className="btn-group mr-2">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_ORDER)}>{t('pages:materials.buttons.setOrdering')}</button>
            </div>
            <div className="btn-group mr-2" role="group" aria-label="First group">
              <button type="button" className="btn btn-secondary" onClick={() => this.setMode(MODE_SET_REFINE_MULTIPLIER)}>{t('pages:materials.buttons.setRefineMultipliers')}</button>
            </div>
          </FinanceOnly>
        </If>

        <If cond={this.state.mode !== MODE_VIEW}>
          <button type="button" className="btn btn-secondary" onClick={() => this.setState({mode: MODE_VIEW})}>{t('common:buttons.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={() => this.save()}>{t('common:buttons.save')}</button>
        </If>
      </div>
    </div>
  }

  renderHelp() {
    if (this.state.mode == MODE_SET_PRICE) {
      return <ul>
        <li>Enter the current Jita price of the material. Buyback prices will be a multipler of this.</li>
        <li>DO NOT adjust these prices to adjust buyback prices, since these prices also determine ship prices. Use "multipliers" instead.</li>
        <li>Ore prices are automatically calculated from refined mineral prices.</li>
        <li>DO NOT enter the "market value" from the main market page - usually way off. Click on the buy and sell orders and enter a price somewhere
          between the lowest sell and highest buy order. Try to ignore low-quantity orders: look for orders with 1000+ items.
        </li>
        <li>Price data from <a href={"https://eve-echoes-market.com/"}>eve-echoes-market.com</a> is displayed as a suggestion. This data is generally pretty
          accurate but sometimes significantly off. Be careful and manually check large changes in price.
        </li>
      </ul>
    } else if (this.state.mode == MODE_SET_TARGET) {
      return <ul>
        <li>Enter the amount of each material we want to always have in inventory. Demand is based on the multiple of this target we have.</li>
        <li>Low demand: less than {globalSettings.highDemandRatio}x; medium demand: less than {globalSettings.lowDemandRatio}x; low demand: greater
          than {globalSettings.lowDemandRatio}x; no demand: target equals zero
        </li>
        <li>Ores cannot be requested. Request the minerals and ore prices will be automatically calculated.</li>
      </ul>
    } else if (this.state.mode == MODE_SET_BUFFER) {
      return <ul>
        <li>Set an amount of the material we want to keep in buffer. This is added to amounts from the queue when setting the target.</li>
        <li>Ores cannot be requested. Request the minerals and ore prices will be automatically calculated.</li>
      </ul>
    } else if (this.state.mode == MODE_LOG) {
      return <ul>
        <li>Enter the amount of each material that you have added to Hangar 2 at one of our bases in any of our corps.</li>
        <li>Compressed ore may be logged by simply entering 100x the volume and Rich ore as 10x the volume.</li>
        <li>{globalSettings.pandacoinRatio * 100}% of payment is in "Pandacoin", which can be spent on corp-manufactured items. The remaining is in ISK
          "balance",
          which may be saved or paid out upon request.
        </li>
        <li>You can paste inventory data directly from the game (tab-delimited format) into the text area below.</li>
      </ul>
    } else if (this.state.mode == MODE_SET_INVENTORY) {
      return <ul>
        <li>Update the amount of each material currently stored in the corp hangar.</li>
        <li>Any inventory amounts you don't change will not be updated.</li>
      </ul>
    } else if (this.state.mode == MODE_SET_ORDER) {
      return <ul>
        <li>Set the order materials appear in the UI. Smaller numbers appear higher.</li>
        <li>We should match the ingame UI when possible.</li>
      </ul>
    } else if (this.state.mode == MODE_SET_MULTIPLIER) {
      return <ul>
        <li>Set a multiplier that will be applied to prices for the materials. This is in addition to demand-based multipliers.</li>
        <li>High demand: {globalSettings.highDemandMultiplier * 100}%; medium demand: {globalSettings.mediumDemandMultiplier * 100}%; low
          demand: {globalSettings.lowDemandMultiplier * 100}%; no demand: {globalSettings.noDemandMultiplier * 100}%
        </li>
      </ul>
    } else if (this.state.mode == MODE_SET_REFINE_MULTIPLIER) {
      return <ul>
        <li>Set the refine efficiency percent that should be used to determine refined value of minerals. Ignored for non-ores.</li>
      </ul>
    } else if (this.state.mode === MODE_VIEW) {
      return <ul>
        <li>Buyback prices for all materials we accept are listed. For planetaries, main price is per mining array per hour, assuming perfect conditions.</li>
        <li>Progress bars show demand. They fill from red (high demand) to green (medium demand) to blue (low demand). Black means no demand.</li>
      </ul>
    }
  }

  render() {
    return (
        <div className="container materials">
          {this.renderToolbar()}
          {this.renderHelp()}

          <Card>
            <Card.Header>
              <ul className="nav nav-tabs card-header-tabs">
                {['Minerals', 'Planetary', 'Datacores', 'Debris'].map(tab => {

                  if (tab === 'Datacores' && (globalSettings.hideDatacoresUnlessAdmin && !this.context.roles.engineer)) {
                    return null;
                  }
                  if (tab === 'Debris' && (globalSettings.hideDebrisUnlessAdmin && !this.context.roles.engineer)) {
                    return null;
                  }

                  return <li className="nav-item" key={tab}>
                    <a className={"nav-link" + (this.state.tab == tab ? " active" : "")} href="#" onClick={evt => {
                      this.setState({
                        tab: tab
                      });
                      evt.preventDefault()
                    }}>{toTitleCase(tab)}</a>
                  </li>
                })}
              </ul>
            </Card.Header>


            {this.renderMaterials(this.context.materials)}
          </Card>
        </div>
    );
  }
}

MaterialsPage.contextType = GlobalInfo;

export default withTranslation(['pages', 'common'])(MaterialsPage);